import { WSEventEnvelope, MarketTick, Contract } from '@starx/shared-types';
import { RestClient } from './rest_client';

export type ConnectionState = 'ONLINE' | 'RECONNECTING' | 'DEGRADED' | 'OFFLINE';

export type WSEventHandler = (envelope: WSEventEnvelope) => void;

export class WSClient {
  private ws: WebSocket | null = null;
  private restClient: RestClient;
  private baseWsURL: string;
  private currentSymbol: string;
  private state: ConnectionState = 'OFFLINE';
  private lastSequence: number = 0;
  private processedEventIds = new Set<string>();
  private listeners = new Map<string, Set<WSEventHandler>>();
  private stateListeners = new Set<(state: ConnectionState) => void>();
  private reconnectAttempts = 0;
  private reconnectTimeout: any = null;
  private pingInterval: any = null;
  private isDestroyed = false;
  private connectionEpoch = 0;

  private operatorID: string = '00000000-0000-0000-0000-000000000001';

  constructor(restClient: RestClient, baseWsURL: string = '', defaultSymbol: string = 'EURUSD_OTC', operatorID: string = '00000000-0000-0000-0000-000000000001') {
    this.restClient = restClient;
    this.baseWsURL = baseWsURL || (window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//' + window.location.host;
    this.currentSymbol = defaultSymbol;
    this.operatorID = operatorID;
  }

  public setOperatorID(opID: string) {
    this.operatorID = opID;
  }

  public subscribe(eventType: string, handler: WSEventHandler): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(handler);
    return () => {
      this.listeners.get(eventType)?.delete(handler);
    };
  }

  public unsubscribe(eventType: string, handler: WSEventHandler): void {
    this.listeners.get(eventType)?.delete(handler);
  }

  public send(payload: any): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(payload));
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  }

  public executeTradeViaWS(req: any, timeoutMs = 2500): Promise<any> {
    return new Promise((resolve, reject) => {
      const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      let timer: any = null;

      const cleanup = () => {
        if (timer) clearTimeout(timer);
        this.unsubscribe('TRADE_CONFIRMED', onConfirm);
        this.unsubscribe('TRADE_REJECTED', onReject);
      };

      const onConfirm = (env: any) => {
        if (env.request_id === requestId || (req.temp_optimistic_id && env.temp_optimistic_id === req.temp_optimistic_id)) {
          cleanup();
          resolve(env.data || env.contract);
        }
      };

      const onReject = (env: any) => {
        if (env.request_id === requestId || (req.temp_optimistic_id && env.temp_optimistic_id === req.temp_optimistic_id)) {
          cleanup();
          reject(new Error(env.error || env.code || 'Trade execution rejected'));
        }
      };

      timer = setTimeout(() => {
        cleanup();
        reject(new Error('WS_TIMEOUT'));
      }, timeoutMs);

      this.subscribe('TRADE_CONFIRMED', onConfirm);
      this.subscribe('TRADE_REJECTED', onReject);

      const sent = this.send({
        action: 'TRADE_EXECUTE',
        request_id: requestId,
        data: req,
      });

      if (!sent) {
        cleanup();
        reject(new Error('WS_NOT_OPEN'));
      }
    });
  }

  public onStateChange(handler: (state: ConnectionState) => void): () => void {
    this.stateListeners.add(handler);
    handler(this.state);
    return () => {
      this.stateListeners.delete(handler);
    };
  }

  private setState(newState: ConnectionState) {
    if (this.state !== newState) {
      this.state = newState;
      this.stateListeners.forEach(fn => fn(newState));
    }
  }

  public getState(): ConnectionState {
    return this.state;
  }

  public getLastSequence(): number {
    return this.lastSequence;
  }

  private isConnecting = false;

  public async connect(): Promise<void> {
    if (this.isDestroyed || this.isConnecting) return;
    this.isConnecting = true;
    clearTimeout(this.reconnectTimeout);
    const epoch = ++this.connectionEpoch;
    const symbol = this.currentSymbol;
    this.setState('RECONNECTING');

    try {
      // 1. Acquire short-lived single-use ticket
      const { ws_ticket } = await this.restClient.getWSTicket();
      if (epoch !== this.connectionEpoch || this.isDestroyed) return;
      const wsUrl = `${this.baseWsURL}/ws/feed?ticket=${encodeURIComponent(ws_ticket)}&symbol=${encodeURIComponent(symbol)}&operator_id=${encodeURIComponent(this.operatorID)}`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        if (epoch !== this.connectionEpoch || this.isDestroyed) return;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.setState('ONLINE');
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        if (epoch !== this.connectionEpoch || this.isDestroyed) return;
        try {
          const envelope: WSEventEnvelope = JSON.parse(event.data);
          this.handleIncomingEnvelope(envelope);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      this.ws.onclose = () => {
        if (epoch !== this.connectionEpoch || this.isDestroyed) return;
        this.isConnecting = false;
        this.cleanupSocket();
        if (!this.isDestroyed) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (err) => {
        if (epoch !== this.connectionEpoch || this.isDestroyed) return;
        this.isConnecting = false;
        this.setState('DEGRADED');
      };
    } catch (err) {
      if (epoch !== this.connectionEpoch || this.isDestroyed) return;
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private handleIncomingEnvelope(envelope: WSEventEnvelope) {
    if ((envelope.type as string).toUpperCase() === 'PONG') {
      return;
    }

    if (envelope.sequence > 0) {
      if (this.lastSequence > 0 && envelope.sequence > this.lastSequence + 1) {
        console.warn(`[WS Gap Detected] Expected ${this.lastSequence + 1}, received ${envelope.sequence}. Triggering resync.`);
        this.dispatch('GAP_DETECTED', envelope);
      }
      this.lastSequence = envelope.sequence;
    }

    // Deduplicate by event_id
    if (this.processedEventIds.has(envelope.event_id)) {
      return; // Ignore duplicate
    }
    this.processedEventIds.add(envelope.event_id);
    if (this.processedEventIds.size > 2000) {
      const iter = this.processedEventIds.values();
      for (let i = 0; i < 500; i++) {
        this.processedEventIds.delete(iter.next().value!);
      }
    }

    // Dispatch to listeners
    this.dispatch(envelope.type, envelope);
    this.dispatch('*', envelope);
  }

  private dispatch(type: string, envelope: WSEventEnvelope) {
    const handlers = this.listeners.get(type);
    if (handlers) {
      handlers.forEach(handler => handler(envelope));
    }
  }

  private startHeartbeat() {
    clearInterval(this.pingInterval);
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);
  }

  private scheduleReconnect() {
    this.setState('RECONNECTING');
    this.reconnectAttempts++;
    // Exponential backoff with jitter: 800ms * 1.5^attempts + jitter (capped at 5s)
    const baseDelay = Math.min(5000, 800 * Math.pow(1.5, Math.min(this.reconnectAttempts, 5)));
    const jitter = Math.random() * 100;
    const delay = baseDelay + jitter;

    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private cleanupSocket() {
    ++this.connectionEpoch;
    this.isConnecting = false;
    clearTimeout(this.reconnectTimeout);
    clearInterval(this.pingInterval);
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }

  public setSymbol(symbol: string) {
    if (this.currentSymbol !== symbol) {
      this.currentSymbol = symbol;
      this.lastSequence = 0;
      this.cleanupSocket();
      this.connect();
    }
  }

  public reconnect() {
    this.cleanupSocket();
    this.setState('RECONNECTING');
    this.connect();
  }

  public simulateGap(sequence: number) {
    this.handleIncomingEnvelope({
      version: '1.0',
      event_id: `GAP-TEST-${Date.now()}`,
      type: 'TICK',
      sequence,
      server_time: new Date().toISOString(),
      symbol: this.currentSymbol,
      data: {
        id: `GAP-TICK-${Date.now()}`,
        tick_id: `GAP-TICK-${Date.now()}`,
        symbol: this.currentSymbol,
        price: 1.08500,
        feed_source: 'SYNTHETIC_OTC',
        feed_sequence: sequence,
        feed_hash: 'GAP-HASH',
        tick_timestamp: new Date().toISOString(),
      }
    });
  }

  public disconnect() {
    this.isDestroyed = true;
    clearTimeout(this.reconnectTimeout);
    this.cleanupSocket();
    this.setState('OFFLINE');
  }
}
